import { useEffect, useState, useCallback } from "react";
import api, { money, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { CurrencyGbp, Check, Sparkle, CaretDown, CaretUp } from "@phosphor-icons/react";

export default function DeferredSuggestions({ onConverted }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/deferred-revenue/suggestions").then(({ data }) => setData(data)).catch(() => setData({ candidates: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const cands = (data?.candidates || []).filter((c) => !c.converted);
  if (!data || cands.length === 0) return null;

  const convert = async (c) => {
    setBusy(c.source_id);
    try {
      await api.post("/deferred-revenue/convert", {
        source_id: c.source_id, doc_number: c.doc_number, contact: c.contact,
        date: c.date, amount: c.amount, description: c.description, revenue_code: c.revenue_code,
      });
      toast.success(`${c.contact} added to Unscheduled — set its recognition schedule`);
      load();
      onConverted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    } finally { setBusy(null); }
  };

  return (
    <div className="bg-white border border-orange/30 rounded-lg mb-5 shadow-sm" data-testid="deferred-suggestions-panel">
      <div role="button" tabIndex={0} onClick={() => setOpen(!open)} onKeyDown={(e) => { if (e.key === "Enter") setOpen(!open); }}
        className="w-full flex items-center justify-between px-5 py-3.5 border-b border-slate-100 cursor-pointer">
        <div className="flex items-center gap-2.5">
          <span className="w-7 h-7 rounded-lg bg-orange/10 text-orangeink flex items-center justify-center"><Sparkle size={16} weight="fill" /></span>
          <div className="text-left">
            <div className="font-heading font-bold text-navy text-base tracking-tight">Income received in advance — detected in Xero</div>
            <div className="text-xs text-navy/70">{cands.length} sales invoice{cands.length === 1 ? "" : "s"} that look like deferred income — add one to schedule its recognition</div>
          </div>
        </div>
        {open ? <CaretUp size={16} className="text-navy/65" /> : <CaretDown size={16} className="text-navy/65" />}
      </div>
      {open && (
        <div className="divide-y divide-navy/10" data-testid="deferred-suggestions-list">
          {cands.map((c) => (
            <div key={c.source_id} data-testid={`deferred-suggestion-${c.source_id}`} className="px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[0.62rem] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${c.confidence === "high" ? "bg-positive/10 text-positive" : "bg-warn/10 text-warn"}`}>{c.confidence}</span>
                  <span className="font-semibold text-navy text-sm">{c.doc_number} · {c.contact}</span>
                </div>
                <div className="text-sm text-navy/70 mt-1">{c.description}</div>
                <div className="font-mono text-xs text-navy/65 mt-1.5">
                  {fmtDate(c.date)} · revenue {c.revenue_code} {c.revenue_name} · suggest spreading over {c.suggested_months} months
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono font-semibold text-navy tabular text-lg">{money(c.amount)}</div>
                <button onClick={() => convert(c)} disabled={busy === c.source_id} data-testid={`deferred-add-${c.source_id}`}
                  className="mt-2 inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg bg-navy text-white hover:bg-orange transition-colors disabled:opacity-60">
                  <Check size={15} weight="bold" /> {busy === c.source_id ? "…" : "Add to schedule"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
